import React, { FC } from 'react';
import { Box, keyframes, LinearProgress, linearProgressClasses, Typography } from '@mui/material';
import Container from '@/components/Container';
import type { SeasonalLoaderProps } from '../../SeasonalTheme';
import { AutumnPumpkin } from './AutumnPumpkin';
import { AutumnLeaf } from './AutumnLeaf';
import { AutumnColors, AUTUMN_GRADIENT } from './autumnColors';

const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-8%) rotate(3deg); }
`;

const drift = keyframes`
  0%   { transform: translate3d(0, -120%, 0) rotate(0deg); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translate3d(var(--sway), 160%, 0) rotate(300deg); opacity: 0; }
`;

const LEAVES = [
  { left: '8%', delay: 0, duration: 2.8, sway: '18px', color: AutumnColors.LEAF_RED },
  { left: '28%', delay: 1.1, duration: 3.4, sway: '-22px', color: AutumnColors.LEAF_YELLOW },
  { left: '66%', delay: 0.5, duration: 3.1, sway: '14px', color: AutumnColors.LEAF_ORANGE },
  { left: '86%', delay: 1.7, duration: 2.6, sway: '-16px', color: AutumnColors.LEAF_BROWN },
];

/** Autumn variant of `components/Loader`: a bobbing pumpkin, drifting leaves and an autumn-coloured progress bar. */
export const AutumnLoader: FC<SeasonalLoaderProps> = ({
  loadingText,
  width = '300px',
  height = '100px',
  progressProps,
}) => {
  const pumpkinSize = Math.max(24, Math.min(72, Math.round(parseInt(height, 10) * 0.6) || 60));
  return (
    <Container column fullHeight>
      <Box
        data-testid='AutumnLoader'
        sx={{
          position: 'relative',
          width,
          maxWidth: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
        {LEAVES.map((leaf) => (
          // Keyframes must be referenced from `sx` (Emotion) to be injected; per-leaf values go in `style`.
          <Box
            key={leaf.left}
            sx={{
              position: 'absolute',
              top: 0,
              lineHeight: 0,
              animation: `${drift} linear infinite both`,
              '@media (prefers-reduced-motion: reduce)': { display: 'none' },
            }}
            style={
              {
                left: leaf.left,
                '--sway': leaf.sway,
                animationDuration: `${leaf.duration}s`,
                animationDelay: `${leaf.delay}s`,
              } as React.CSSProperties
            }>
            <AutumnLeaf size={Math.round(pumpkinSize * 0.33)} color={leaf.color} />
          </Box>
        ))}
        <Box
          sx={{
            display: 'inline-flex',
            animation: `${bob} 1.6s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}>
          <AutumnPumpkin size={pumpkinSize} />
        </Box>
      </Box>
      <LinearProgress
        variant='indeterminate'
        {...progressProps}
        sx={{
          width: '100%',
          height: 6,
          borderRadius: 999,
          backgroundColor: 'rgba(232, 115, 26, 0.15)',
          // Only recolour the bar. Never touch `animation` here: MUI's indeterminate movement lives on the same
          // element and overriding it collapses the bar to zero width.
          [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 999,
            backgroundImage: AUTUMN_GRADIENT,
          },
          ...(progressProps?.sx as object),
        }}
      />
      {loadingText && <Typography>{loadingText}</Typography>}
    </Container>
  );
};

export default AutumnLoader;
